import React from 'react';
import { motion } from 'framer-motion';

interface GaugeProps {
  label: string;
  value: number;
  max: number;
  color: string;
  size?: number;
}

const MiniGauge: React.FC<GaugeProps> = ({ label, value, max, color, size = 140 }) => {
  const percentage = Math.min(100, Math.max(0, (value / max) * 100));

  // Semi-circle gauge: 180° (left/bottom) to 0° (right/bottom)
  // This creates a proper half-circle arc at the bottom
  const startAngle = 180; // Left side (bottom-left)
  const endAngle = 0;     // Right side (bottom-right)

  // Current angle based on percentage (goes from 180° to 0°)
  const currentAngle = startAngle - (percentage / 100) * startAngle;

  const strokeWidth = 12;
  const radius = (size - strokeWidth * 2) / 2;
  const centerX = size / 2;
  const centerY = size / 2;

  // Calculate needle end point
  const needleLength = radius - 5;
  const angleRad = (currentAngle * Math.PI) / 180;
  const needleEndX = centerX + needleLength * Math.cos(angleRad);
  const needleEndY = centerY - needleLength * Math.sin(angleRad); // Negative because SVG Y is inverted

  // Create arc path for semi-circle
  const createArcPath = (startDeg: number, endDeg: number) => {
    const start = (startDeg * Math.PI) / 180;
    const end = (endDeg * Math.PI) / 180;

    const startX = centerX + radius * Math.cos(start);
    const startY = centerY - radius * Math.sin(start); // Negative for SVG coordinates
    const endX = centerX + radius * Math.cos(end);
    const endY = centerY - radius * Math.sin(end);

    // For 180° arc, always use large arc flag = 0
    const largeArcFlag = 0;

    return `M ${startX} ${startY} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${endX} ${endY}`;
  };

  return (
    <div className="flex flex-col items-center gap-3">
      <div className="relative" style={{ width: size, height: size / 2 + 20 }}>
        <svg
          width={size}
          height={size / 2 + 20}
          viewBox={`0 0 ${size} ${size / 2 + 20}`}
          style={{ overflow: 'visible' }}
        >
          <defs>
            <linearGradient id={`gauge-gradient-${label}`} x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor={color} stopOpacity="0.7" />
              <stop offset="100%" stopColor={color} stopOpacity="1" />
            </linearGradient>
            <filter id={`gauge-shadow-${label}`}>
              <feGaussianBlur in="SourceAlpha" stdDeviation="2"/>
              <feOffset dx="0" dy="1" result="offsetblur"/>
              <feComponentTransfer>
                <feFuncA type="linear" slope="0.3"/>
              </feComponentTransfer>
              <feMerge>
                <feMergeNode/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
          </defs>

          {/* Background Arc - full 180° semi-circle */}
          <path
            d={createArcPath(startAngle, endAngle)}
            fill="none"
            stroke="#E5E7EB"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
          />

          {/* Filled background arc - light color showing percentage */}
          <motion.path
            d={createArcPath(startAngle, startAngle)}
            animate={{ d: createArcPath(startAngle, currentAngle) }}
            fill="none"
            stroke={color}
            strokeOpacity="0.2"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            transition={{ duration: 1.5, ease: 'easeOut' }}
          />

          {/* Progress Arc - vibrant color on top */}
          <motion.path
            d={createArcPath(startAngle, startAngle)}
            animate={{ d: createArcPath(startAngle, currentAngle) }}
            fill="none"
            stroke={`url(#gauge-gradient-${label})`}
            strokeWidth={strokeWidth - 4}
            strokeLinecap="round"
            transition={{ duration: 1.5, ease: 'easeOut' }}
            filter={`url(#gauge-shadow-${label})`}
          />

          {/* Needle - rendered AFTER arcs so it's on top */}
          <motion.line
            x1={centerX}
            y1={centerY}
            x2={centerX + needleLength * Math.cos((startAngle * Math.PI) / 180)}
            y2={centerY - needleLength * Math.sin((startAngle * Math.PI) / 180)}
            animate={{
              x2: needleEndX,
              y2: needleEndY,
            }}
            stroke="#1F2937"
            strokeWidth="3"
            strokeLinecap="round"
            transition={{ duration: 1.5, ease: 'easeOut' }}
          />

          {/* Needle tip - colored circle */}
          <motion.circle
            cx={centerX + needleLength * Math.cos((startAngle * Math.PI) / 180)}
            cy={centerY - needleLength * Math.sin((startAngle * Math.PI) / 180)}
            animate={{
              cx: needleEndX,
              cy: needleEndY,
            }}
            r="5"
            fill={color}
            transition={{ duration: 1.5, ease: 'easeOut' }}
          />

          {/* Center pivot - rendered LAST so it's on top of needle */}
          <circle cx={centerX} cy={centerY} r="8" fill="#1F2937" />
          <circle cx={centerX} cy={centerY} r="4" fill="white" />
        </svg>
      </div>

      {/* Value Display - BELOW the gauge, not overlapping */}
      <div className="text-center">
        <motion.div
          className="text-2xl font-bold flex items-baseline justify-center gap-0.5"
          style={{ color }}
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.8 }}
        >
          <span>{percentage.toFixed(0)}</span>
          <span className="text-base">%</span>
        </motion.div>
      </div>

      <div className="text-sm font-semibold text-gray-800 text-center">{label}</div>
      <div className="text-xs text-gray-500 text-center">
        {value.toLocaleString()} / {max.toLocaleString()}
      </div>
    </div>
  );
};

interface GaugeClusterProps {
  title: string;
  gauges: Array<{
    label: string;
    value: number;
    max: number;
    color: string;
  }>;
}

const GaugeCluster: React.FC<GaugeClusterProps> = ({ title, gauges }) => {
  return (
    <motion.div
      className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <h3 className="text-lg font-bold text-gray-900 mb-6">{title}</h3>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
        {gauges.map((gauge, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: index * 0.1 }}
          >
            <MiniGauge {...gauge} />
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
};

export default GaugeCluster;

